import { useMemo } from "react"
import { useAuth } from "@/hooks/use-auth"
import {
  isAdminRole,
  userCan,
  userCanRead,
  type PermissionModule,
  type PermissionOperation,
} from "@/lib/permissions"

export interface Permissions {
  /** Full-access admin role (bypasses every check). */
  isAdmin: boolean
  /** Does the current user hold `<module>.<operation>` ? */
  can: (module: PermissionModule, operation: PermissionOperation) => boolean
  /** Can the current user open/read `module` at all. */
  canRead: (module: PermissionModule) => boolean
}

/**
 * Permission-aware UI gate for the current user. Reads the authoritative
 * backend permission list from the auth context and exposes pure predicates to
 * hide menus / routes / tabs / action buttons the user cannot use. An
 * unauthenticated context denies everything (no user = no permissions).
 *
 * Example:
 *   const { can, canRead } = usePermissions()
 *   {canRead("commercial") && <NavItem href="/commercial" />}
 *   {can("training", "delete") && <DeleteButton />}
 */
export function usePermissions(): Permissions {
  const { user } = useAuth()

  return useMemo<Permissions>(() => {
    if (user === null) {
      return {
        isAdmin: false,
        can: () => false,
        canRead: () => false,
      }
    }
    return {
      isAdmin: isAdminRole(user.role),
      can: (module, operation) => userCan(user, module, operation),
      canRead: (module) => userCanRead(user, module),
    }
  }, [user])
}
